import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { A11yProvider } from './lib/a11y';
import { fetchSession, forwardLoginCallback, login } from './lib/auth';
import { config } from './lib/config';
import { ThemeProvider } from './lib/theme';
import AppShell, { APP_STORE_URL, PLAY_STORE_URL } from './components/AppShell';
import DivakoHandwrite from './components/DivakoHandwrite';
import BillingPage from './pages/BillingPage';
import HomePage from './pages/HomePage';
import MeterDetailPage from './pages/MeterDetailPage';
import NotificationsPage from './pages/NotificationsPage';
import ProfilePage from './pages/ProfilePage';

// Error splash (auth failures) — card with logo + message.
function Splash({ title, text }: { title: string; text: string }): ReactElement {
  return (
    <main className="centered">
      <div className="splash-card">
        <img className="splash-logo only-dark" src="/logo_white_green.svg" alt="Divako Portal" />
        <img className="splash-logo only-light" src="/logo-light-bg.svg" alt="Divako Portal" />
        <h2>{title}</h2>
        <p>{text}</p>
      </div>
    </main>
  );
}

// Loading splash (session check / sign-in redirect) — nothing but the Divako
// handwriting animation, matching the application repo's DataTables loader.
function SplashLoading(): ReactElement {
  return (
    <main className="centered">
      <DivakoHandwrite size={180} />
    </main>
  );
}

// The legacy mobile app was a WebView onto the old PHP portal, whose host
// (portal.divako.io) now serves this SPA — so users who never updated land
// here. It identifies itself with the User-Agent the old portal's index.php
// parsed: `DivakoMobile/Portal/<osVer>;<deviceType>;<model>;<platform>;<appVer>`.
// Those users can't use the web portal (their WebView would just bounce into
// the OIDC login); show them a full-screen "update the app" splash instead.
// Same store listings as the legacy portal's mobile-update page — the new
// app ships as an update under both.
const LEGACY_APP_UA = 'DivakoMobile/Portal';

function legacyAppPlatform(ua: string): 'android' | 'ios' | null {
  const platform = (ua.split('/')[2] ?? '').split(';')[3]?.trim().toLowerCase();
  if (platform === 'android' || platform === 'ios') return platform;
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod|ios/i.test(ua)) return 'ios';
  return null;
}

function LegacyAppUpdate(): ReactElement {
  const { t } = useTranslation();
  // No target="_blank": the old app's WebView handles in-frame navigation to
  // the store reliably; a popup may go nowhere.
  const platform = legacyAppPlatform(navigator.userAgent);
  return (
    <main className="centered">
      <div className="splash-card">
        <img className="splash-logo only-dark" src="/logo_white_green.svg" alt="Divako Portal" />
        <img className="splash-logo only-light" src="/logo-light-bg.svg" alt="Divako Portal" />
        <h2>{t('ui.updateRequired')}</h2>
        <p>{t('ui.appOutdated')}</p>
        <span className="store-badges">
          {platform !== 'ios' && (
            <a href={PLAY_STORE_URL}>
              <img alt="Get it on Google Play" src="/googleplay_badge.svg" />
            </a>
          )}
          {platform !== 'android' && (
            <a href={APP_STORE_URL}>
              <img alt="Download on the App Store" src="/appstore_badge.svg" />
            </a>
          )}
        </span>
      </div>
    </main>
  );
}

// Route guard — the old portal's behaviour (index.php: isLogin() ? page :
// RedirectToLoginPage()). We QUERY the session first (fetchSession = "is the
// user connected?"), then act:
//   authenticated         → render the app
//   logged out + auth up  → bounce straight into the server-driven login
//   auth unavailable      → show a message (NEVER bounce to a dead /api/auth/*)
type GateState = 'loading' | 'authed' | 'redirecting' | 'unavailable';

// Hold the loading splash at least this long, so the handwriting animation
// gets one full "divako.io" pass (draw ~1.8s + hold) instead of flashing for
// the few hundred ms a fast session check takes.
const MIN_SPLASH_MS = 2000;

function RequireSession({ children }: { children: ReactElement }): ReactElement {
  const [state, setState] = useState<GateState>('loading');
  const [minElapsed, setMinElapsed] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setMinElapsed(true), MIN_SPLASH_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    let alive = true;
    fetchSession().then((s) => {
      if (!alive) return;

      if (s.authenticated) {
        setState('authed');
      } else if (s.available) {
        // Logged out but web auth is up → automatic login, like the old
        // portal's RedirectToLoginPage(). Come back to the page they wanted.
        setState('redirecting');
      } else {
        // Web auth isn't up (e.g. /api/auth/session 404s). Don't redirect.
        setState('unavailable');
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  // The session check runs concurrently with the splash, but nothing visible
  // happens — no app render, no login redirect, no error — until the
  // animation has had its full pass.
  useEffect(() => {
    if (state === 'redirecting' && minElapsed) {
      login(window.location.pathname + window.location.search);
    }
  }, [state, minElapsed]);

  if (state === 'authed' && minElapsed) return children;
  if (state === 'unavailable' && minElapsed) {
    return (
      <Splash
        title="Sign-in unavailable"
        text={`Could not reach sign-in (${config.appEnv}). It may not be configured yet — please try again shortly.`}
      />
    );
  }
  return <SplashLoading />;
}

// OAuth callback landing. The client registered at login.divako.io redirects
// back HERE (SPA host, /auth/callback) — not to portal-api. The SPA can't
// complete the exchange itself (no client_secret in the browser), so it
// forwards code/state to portal-api's /api/auth/callback, which sets the
// session cookie and 302s back to `/`. IdP errors (?error=…) are shown here
// rather than forwarded — with automatic login a raw API error page would be
// a dead end. Direct hits without ?code just go home.
function AuthCallback(): ReactElement {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const err = q.get('error');
    if (err) setError(q.get('error_description') || err);
    else if (q.has('code')) forwardLoginCallback();
    else window.location.replace('/');
  }, []);

  if (error) return <Splash title="Sign-in failed" text={error} />;
  return <SplashLoading />;
}

export default function App(): ReactElement {
  if (navigator.userAgent.includes(LEGACY_APP_UA)) {
    return (
      <ThemeProvider>
        <LegacyAppUpdate />
      </ThemeProvider>
    );
  }
  return (
    <ThemeProvider>
      <A11yProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route
              element={
                <RequireSession>
                  <AppShell />
                </RequireSession>
              }
            >
              <Route path="/" element={<HomePage />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/meter/:mid" element={<MeterDetailPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </A11yProvider>
    </ThemeProvider>
  );
}
